import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Project, ModuleType } from '@/types'
import { Building2, Settings2 } from 'lucide-react'
import { isSuperAdmin, isAdmin, hasAdminPrivileges } from '@/utils/permissions'

interface ProjectListRowProps {
  project: Project
  user: any
}

const MODULE_CONFIG: Record<ModuleType, { label: string; color: string }> = {
  build: { label: 'Build', color: 'bg-blue-400' },
  review: { label: 'Review', color: 'bg-amber-400' },
  manage: { label: 'Manage', color: 'bg-emerald-400' },
}

export const ProjectListRow = ({ project, user }: ProjectListRowProps) => {
  const { t } = useTranslation(['dashboard', 'common'])
  const navigate = useNavigate()
  const userHasAdminPrivileges = hasAdminPrivileges(user)

  const enabledModules = project.enabled_modules || ['build', 'review', 'manage']

  const handleClick = () => {
    navigate(`/project/${project.id}`)
  }

  return (
    <div
      onClick={handleClick}
      className="group flex items-center gap-4 px-4 py-3 bg-framed-neutral-100 rounded-2xl hover:bg-framed-neutral-200 transition-colors duration-150 cursor-pointer"
    >
      {/* Name + Agency */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-framed-neutral-800 truncate group-hover:text-framed-neutral-800-300 transition-colors">
            {project?.name || t('dashboard:project.untitled')}
          </h3>
          {isSuperAdmin(user) && (project as any).agencies?.name && (
            <span className="hidden sm:inline-flex items-center gap-1 text-xs text-framed-neutral-500 flex-shrink-0">
              <Building2 className="w-3 h-3" />
              {(project as any).agencies.name}
            </span>
          )}
          {!isSuperAdmin(user) && isAdmin(user) && project.agency_id !== user?.agency_id && (
            <span className="hidden sm:inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 text-[10px] font-medium flex-shrink-0">
              <Building2 className="w-2.5 h-2.5" />
              {t('dashboard:project.externalAgency')}
            </span>
          )}
        </div>
      </div>

      {/* Module Badges */}
      <div className="hidden md:flex items-center gap-1.5 flex-shrink-0">
        {enabledModules.map((mod) => {
          const config = MODULE_CONFIG[mod]
          if (!config) return null
          return (
            <span
              key={mod}
              className="inline-flex items-center gap-1 text-[11px] text-framed-neutral-500"
            >
              <span className={`w-1.5 h-1.5 rounded-full ${config.color}`} />
              {config.label}
            </span>
          )
        })}
      </div>

      {/* Updated */}
      <div className="hidden sm:block w-20 flex-shrink-0 text-right">
        <span className="text-xs text-framed-neutral-400">
          {new Date(project.updated_at).toLocaleDateString('en-US', {
            day: 'numeric',
            month: 'short'
          })}
        </span>
      </div>

      {/* Settings */}
      {userHasAdminPrivileges && (
        <button
          onClick={(e) => {
            e.stopPropagation()
            navigate(`/project/${project.id}/settings`)
          }}
          className="p-1.5 rounded text-framed-neutral-400 hover:text-framed-neutral-800 hover:bg-framed-neutral-100 transition-colors flex-shrink-0"
          title={t('dashboard:project.settings') || 'Settings'}
        >
          <Settings2 className="w-4 h-4" />
        </button>
      )}
    </div>
  )
}
